/**
 * @fileoverview Interactive user milestone card with action buttons
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Edit, CheckCircle, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Milestone } from '@/lib/types/milestone';
import { format } from 'date-fns';

interface UserMilestoneCardProps {
  milestone: Milestone;
  jobId: string;
  jobPublicId: string;
  canEdit: boolean;
  canRequestChange: boolean;
  canMarkComplete: boolean;
  canUploadFiles: boolean;
  isFullyApproved: boolean;
  onEdit?: (milestone: Milestone) => void;
  onMarkComplete?: (milestoneId: string) => void;
  onRequestChange?: (milestone: Milestone) => void;
}

export function UserMilestoneCard({
  milestone,
  jobId,
  jobPublicId,
  canEdit,
  canRequestChange,
  canMarkComplete,
  canUploadFiles,
  isFullyApproved,
  onEdit,
  onMarkComplete,
  onRequestChange,
}: UserMilestoneCardProps) {
  const navigate = useNavigate();
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);

  const handleMarkComplete = async () => {
    if (!onMarkComplete) return;
    setIsMarkingComplete(true);
    try {
      await onMarkComplete(milestone.id);
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const handleUploadFiles = () => {
    // Add timestamp to force navigation even if already on the same URL
    navigate(`/jobs/${jobPublicId}?tab=documents&subtab=upload&t=${Date.now()}`);
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      planning: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      design: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      development: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      review: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      testing: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
      delivery: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      client_approval: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
      installation: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
    };
    return colors[type] || 'bg-secondary text-secondary-foreground';
  };

  const getOwnerColor = (owner: string) => {
    return owner === 'client'
      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
  };

  // Pre-approval: can edit directly
  const showPreApprovalActions = !isFullyApproved && !milestone.is_completed && !milestone.is_locked;
  // Post-approval: can only request changes, mark complete, upload
  const showPostApprovalActions = isFullyApproved && !milestone.is_completed && !milestone.is_locked;

  return (
    <Card className="border-l-4 border-l-primary">
      <CardContent className="p-4">
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-lg">{milestone.milestone_name}</h4>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <Badge variant="outline" className={getTypeColor(milestone.milestone_type)}>
                  {milestone.milestone_type}
                </Badge>
                <Badge variant="outline" className={getOwnerColor(milestone.action_owner)}>
                  {milestone.action_owner === 'client' ? 'Client' : 'Vendor'}
                </Badge>
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>Due: {format(new Date(milestone.due_date), 'MMM d, yyyy')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div>
              <span className="text-sm font-medium">Deliverable:</span>
              <p className="text-sm text-muted-foreground mt-1">{milestone.deliverable}</p>
            </div>
            {milestone.description && (
              <div>
                <span className="text-sm font-medium">Description:</span>
                <p className="text-sm text-muted-foreground mt-1">{milestone.description}</p>
              </div>
            )}
          </div>

          {/* Pre-approval: Edit button only */}
          {showPreApprovalActions && canEdit && onEdit && (
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(milestone)}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
            </div>
          )}

          {/* Post-approval: Request Change, Mark Complete, Upload */}
          {showPostApprovalActions && (
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              {canMarkComplete && onMarkComplete && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleMarkComplete}
                  disabled={isMarkingComplete}
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {isMarkingComplete ? 'Completing...' : 'Mark Complete'}
                </Button>
              )}
              {canUploadFiles && milestone.show_upload_button && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUploadFiles}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Upload Files
                </Button>
              )}
              {canRequestChange && onRequestChange && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRequestChange(milestone)}
                >
                  Request Change
                </Button>
              )}
            </div>
          )}

          {milestone.is_completed && (
            <div className="flex items-center gap-2 pt-2 border-t text-sm text-muted-foreground">
              <CheckCircle className="h-4 w-4 text-primary" />
              <span>Completed {format(new Date(milestone.completed_at!), 'MMM d, yyyy')}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
